import { html } from "../../render";
import { attr, cn, safeRandomId, spreadAttrs, styleVars } from "../../utils";
import { LpButton } from "../button";
import { LpIcon } from "../icon";
export const LP_DROPDOWN_VARIANTS = {
    item: ["default", "danger"],
    align: ["start", "center", "end"],
    side: ["top", "bottom", "left", "right"],
};
export const LP_DROPDOWN_DEFAULT_VARIANTS = {
    item: "default",
    align: "start",
    side: "bottom",
};
const rootManagedAttrs = ["data-lp-component", "class", "style"];
const contentManagedAttrs = [
    "data-lp-part",
    "class",
    "id",
    "popover",
    "role",
    "aria-label",
    "tabindex",
    "data-align",
    "data-side",
    "style",
];
const itemManagedAttrs = [
    "data-lp-part",
    "class",
    "type",
    "role",
    "data-variant",
    "data-inset",
    "data-selected",
    "data-lp-dropdown-value",
    "data-close-on-click",
    "disabled",
    "aria-disabled",
    "aria-checked",
    "href",
    "target",
    "rel",
];
function getComputedRel(rel, target) {
    if (rel)
        return rel;
    return target === "_blank" ? "noopener noreferrer" : undefined;
}
function renderItemContent({ label, icon, selected = false, }) {
    return html `
        ${LpIcon({ name: icon, size: 16, className: "lp-dropdown_item-icon" })}
        <span class="lp-dropdown_item-label">${label}</span>
        ${selected
        ? html `
                <span class="lp-dropdown_item-indicator" aria-hidden="true">
                    ${LpIcon({ name: "check", size: 16 })}
                </span>
            `
        : ""}
    `;
}
function renderRoot(tagName, component, { trigger, content, className, attributes, }) {
    const id = safeRandomId();
    const context = { contentId: `lp-dropdown-content-${id}` };
    const style = [
        styleVars({ "--lp-dropdown-anchor-name": `--lp-dropdown-anchor-${id}` }),
        typeof attributes?.style === "string" ? attributes.style : undefined,
    ].filter(Boolean).join(" ");
    return html `
        <${tagName}
            ${attr("data-lp-component", component)}
            class="${cn("lp-dropdown", tagName === "lp-dropdown-sub" && "lp-dropdown_sub", className, attributes?.class)}"
            ${attr("style", style)}
            ${spreadAttrs(attributes, { omit: rootManagedAttrs })}
        >
            ${trigger(context)}
            ${content(context)}
        </${tagName}>
    `;
}
function LpDropdownRoot(props) {
    return renderRoot("lp-dropdown", "Dropdown", props);
}
function LpDropdownTrigger({ contentId, ...buttonProps }) {
    return LpButton({
        ...buttonProps,
        className: cn("lp-dropdown_trigger", buttonProps.className),
        attributes: {
            ...buttonProps.attributes,
            "data-lp-part": "trigger",
            "popovertarget": contentId,
            "aria-haspopup": "menu",
            "aria-expanded": "false",
        },
    });
}
function renderDropdownContent({ contentId, children, label, align = LP_DROPDOWN_DEFAULT_VARIANTS.align, side, sideOffset, alignOffset = 0, className, attributes, }, part) {
    const resolvedSide = side
        ?? (part === "subcontent" ? "right" : LP_DROPDOWN_DEFAULT_VARIANTS.side);
    const resolvedSideOffset = sideOffset ?? (part === "subcontent" ? 4 : 8);
    const style = [
        styleVars({
            "--lp-dropdown-side-offset": `${Math.max(0, resolvedSideOffset)}px`,
            "--lp-dropdown-align-offset": `${alignOffset}px`,
        }),
        typeof attributes?.style === "string" ? attributes.style : undefined,
    ].filter(Boolean).join(" ");
    return html `
        <div
            data-lp-part="${part}"
            class="${cn("lp-dropdown_content", part === "subcontent" && "lp-dropdown_subcontent", className, attributes?.class)}"
            id="${contentId}"
            popover="auto"
            role="menu"
            ${attr("aria-label", label)}
            tabindex="-1"
            data-side="${resolvedSide}"
            data-align="${align}"
            ${attr("style", style)}
            ${spreadAttrs(attributes, { omit: contentManagedAttrs })}
        >${children}</div>
    `;
}
function LpDropdownContent(props) {
    return renderDropdownContent(props, "content");
}
function LpDropdownItem({ label, icon, variant = LP_DROPDOWN_DEFAULT_VARIANTS.item, selected = false, inset = false, disabled = false, closeOnClick = true, value, className, attributes, }) {
    return html `
        <button
            data-lp-part="item"
            class="${cn("lp-dropdown_item", className, attributes?.class)}"
            type="button"
            role="menuitem"
            ${attr("data-variant", variant)}
            ${attr("data-inset", inset)}
            ${attr("data-selected", selected)}
            ${attr("data-lp-dropdown-value", value)}
            ${attr("data-close-on-click", closeOnClick ? undefined : "false")}
            ${attr("disabled", disabled)}
            ${attr("aria-disabled", disabled ? "true" : undefined)}
            ${spreadAttrs(attributes, { omit: itemManagedAttrs })}
        >${renderItemContent({ label, icon, selected })}</button>
    `;
}
function LpDropdownLinkItem({ label, icon, variant = LP_DROPDOWN_DEFAULT_VARIANTS.item, inset = false, value, className, attributes, href, target, rel, }) {
    return html `
        <a
            data-lp-part="item"
            class="${cn("lp-dropdown_item", "lp-dropdown_link", className, attributes?.class)}"
            role="menuitem"
            href="${href}"
            ${attr("target", target)}
            ${attr("rel", getComputedRel(rel, target))}
            ${attr("data-variant", variant)}
            ${attr("data-inset", inset)}
            ${attr("data-lp-dropdown-value", value)}
            ${spreadAttrs(attributes, { omit: itemManagedAttrs })}
        >${renderItemContent({ label, icon })}</a>
    `;
}
function LpDropdownCheckboxItem({ label, checked = false, disabled = false, closeOnClick = false, value, className, attributes, }) {
    return html `
        <button
            data-lp-part="item"
            class="${cn("lp-dropdown_item", "lp-dropdown_choice", className, attributes?.class)}"
            type="button"
            role="menuitemcheckbox"
            aria-checked="${String(checked)}"
            ${attr("data-lp-dropdown-value", value)}
            ${attr("data-close-on-click", closeOnClick ? undefined : "false")}
            ${attr("disabled", disabled)}
            ${attr("aria-disabled", disabled ? "true" : undefined)}
            ${spreadAttrs(attributes, { omit: itemManagedAttrs })}
        >
            <span class="lp-dropdown_choice-indicator" aria-hidden="true">
                ${LpIcon({ name: "check", size: 14 })}
            </span>
            <span class="lp-dropdown_item-label">${label}</span>
        </button>
    `;
}
function LpDropdownRadioItem({ label, value, checked = false, disabled = false, closeOnClick = false, icon, inset = false, className, attributes, }) {
    return html `
        <button
            data-lp-part="item"
            class="${cn("lp-dropdown_item", "lp-dropdown_radio", className, attributes?.class)}"
            type="button"
            role="menuitemradio"
            aria-checked="${String(checked)}"
            ${attr("data-lp-dropdown-value", value)}
            ${attr("data-close-on-click", closeOnClick ? undefined : "false")}
            ${attr("data-inset", inset)}
            ${attr("disabled", disabled)}
            ${attr("aria-disabled", disabled ? "true" : undefined)}
            ${spreadAttrs(attributes, { omit: itemManagedAttrs })}
        >
            ${LpIcon({ name: icon, size: 16, className: "lp-dropdown_item-icon" })}
            <span class="lp-dropdown_item-label">${label}</span>
            <span class="lp-dropdown_radio-indicator" aria-hidden="true">
                ${LpIcon({ name: "check", size: 16 })}
            </span>
        </button>
    `;
}
function LpDropdownGroup({ children, className, attributes, }) {
    const context = { labelId: `lp-dropdown-label-${safeRandomId()}` };
    return html `
        <div
            class="${cn("lp-dropdown_group", className, attributes?.class)}"
            role="group"
            aria-labelledby="${context.labelId}"
            ${spreadAttrs(attributes, { omit: ["class", "role", "aria-labelledby"] })}
        >
            ${children(context)}
        </div>
    `;
}
function LpDropdownLabel({ labelId, children, action, inset = false, className, attributes, }) {
    return html `
        <div class="${cn("lp-dropdown_group-head", className, attributes?.class)}" ${spreadAttrs(attributes, { omit: ["class"] })}>
            <div class="lp-dropdown_label" id="${labelId}" role="presentation" ${attr("data-inset", inset)}>${children}</div>
            ${action ? html `<div class="lp-dropdown_group-action">${action}</div>` : ""}
        </div>
    `;
}
function LpDropdownRadioGroup({ children, label, className, attributes, }) {
    return html `
        <div
            class="${cn("lp-dropdown_radio-group", className, attributes?.class)}"
            role="group"
            ${attr("aria-label", label)}
            ${spreadAttrs(attributes, { omit: ["class", "role", "aria-label"] })}
        >${children}</div>
    `;
}
function LpDropdownSub(props) {
    return renderRoot("lp-dropdown-sub", undefined, { side: "bottom", ...props });
}
function LpDropdownSubTrigger({ contentId, label, icon, variant = LP_DROPDOWN_DEFAULT_VARIANTS.item, inset = false, disabled = false, value, className, attributes, }) {
    return html `
        <button
            data-lp-part="subtrigger"
            class="${cn("lp-dropdown_item", "lp-dropdown_subtrigger", className, attributes?.class)}"
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded="false"
            popovertarget="${contentId}"
            ${attr("data-variant", variant)}
            ${attr("data-inset", inset)}
            ${attr("data-lp-dropdown-value", value)}
            ${attr("disabled", disabled)}
            ${attr("aria-disabled", disabled ? "true" : undefined)}
            ${spreadAttrs(attributes, { omit: [...itemManagedAttrs, "popovertarget", "aria-haspopup", "aria-expanded"] })}
        >
            ${LpIcon({ name: icon, size: 16, className: "lp-dropdown_item-icon" })}
            <span class="lp-dropdown_item-label">${label}</span>
            <span class="lp-dropdown_subtrigger-icon" aria-hidden="true">
                ${LpIcon({ name: "caret-right", size: 16 })}
            </span>
        </button>
    `;
}
function LpDropdownSubContent(props) {
    return renderDropdownContent(props, "subcontent");
}
function LpDropdownSeparator({ className, attributes, } = {}) {
    return html `
        <div
            class="${cn("lp-dropdown_separator", className, attributes?.class)}"
            role="separator"
            ${spreadAttrs(attributes, { omit: ["class", "role"] })}
        ></div>
    `;
}
export const LpDropdownMenu = {
    Root: LpDropdownRoot,
    Trigger: LpDropdownTrigger,
    Content: LpDropdownContent,
    Item: LpDropdownItem,
    LinkItem: LpDropdownLinkItem,
    CheckboxItem: LpDropdownCheckboxItem,
    RadioGroup: LpDropdownRadioGroup,
    RadioItem: LpDropdownRadioItem,
    Group: LpDropdownGroup,
    Label: LpDropdownLabel,
    Separator: LpDropdownSeparator,
    Sub: LpDropdownSub,
    SubTrigger: LpDropdownSubTrigger,
    SubContent: LpDropdownSubContent,
};
